import { type V3Milestone } from '@apps/web/features/phoebe_chat_v3/model/activity_presentation';
import {
    type V3ReasoningEntry,
    type V3ReasoningNarration,
    type V3ReasoningStep,
} from '@apps/web/features/phoebe_chat_v3/model/transcript';
import { normalizeVisibleText } from '@apps/web/features/phoebe_chat_v3/model/visible_text';

export type MutableReasoningEntry = {
    readonly id: string;
    readonly createdAt: string;
    readonly durationsMs: Array<number>;
    readonly steps: Array<V3ReasoningStep>;
    readonly pendingNarrations: Array<V3ReasoningNarration>;
};

export function displayReasoning(value: string): string | null {
    return normalizeVisibleText(value, true);
}

export function createMutableReasoningEntry({
    id,
    createdAt,
}: {
    readonly id: string;
    readonly createdAt: string;
}): MutableReasoningEntry {
    return {
        id,
        createdAt,
        durationsMs: [],
        steps: [],
        pendingNarrations: [],
    };
}

export function bufferReasoning(
    entry: MutableReasoningEntry,
    narration: V3ReasoningNarration,
): void {
    entry.pendingNarrations.push(narration);
}

export function flushPendingReasoning(entry: MutableReasoningEntry): void {
    if (entry.pendingNarrations.length === 0) {
        return;
    }
    entry.steps.push(...entry.pendingNarrations);
    entry.pendingNarrations.length = 0;
}

export function appendMilestone(
    entry: MutableReasoningEntry,
    milestone: V3Milestone,
): void {
    const narrations = entry.pendingNarrations.splice(0);
    entry.steps.push({
        type: 'milestone',
        ...milestone,
        narrations,
    });
}

export function finalizedReasoningEntry(
    entry: MutableReasoningEntry,
): V3ReasoningEntry {
    flushPendingReasoning(entry);
    const durationMs = entry.durationsMs.length
        ? entry.durationsMs.reduce((total, duration) => total + duration, 0)
        : null;
    return {
        kind: 'reasoning',
        id: entry.id,
        createdAt: entry.createdAt,
        durationMs,
        steps: entry.steps,
        revision: JSON.stringify([durationMs, entry.steps]),
    };
}
